import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {validateFields, validateField} from '../../../../utilities/validation/validator';
import authenticationProvider from '../../../../utilities/authentication/authenticationProvider';
import ProviderManager from '../../../../provider/providerManager';
import lodash from 'lodash';

export const getEMVStep1 = createAsyncThunk('paymentCG/getEMVStep1', async (data, thunkAPI) => {
  try {
    var token = authenticationProvider.getSavedToken();
    var result;

    let totalPrice = data.totalPrice && data.totalPrice != '' ? data.totalPrice : '0';
    let numberOfPayments = data.numberOfPayments && data.numberOfPayments != '' ? data.numberOfPayments : '0';

    if (!token)
      result = await ProviderManager.getProviderDataExtended(
        'getCGEMVStep1',
        [data.type, data.id, totalPrice, numberOfPayments, 'sale', data.isIDF || false, data.source],
        [{key: 'x-cell-recaptcha-token', value: data.token}],
      );
    else
      result = await ProviderManager.getProviderDataExtended(
        'getCGEMVStep1',
        [data.type, data.id, totalPrice, numberOfPayments, 'sale', data.isIDF || false, data.source],
        [
          {key: 'x-cell-recaptcha-token', value: data.token},
          {key: 'authorization', value: token},
        ],
      );

    return result.Body;
  } catch (error) {
    return error;
  }
});

export const getEMVStep2 = createAsyncThunk('paymentCG/getEMVStep2', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderDataExtended(
      'getCGEMVStep2',
      [data.total, data.token, data.type, data.isShowIdNumber, data.id, data.blockId, 'sale', data.isIDF || false, data.source],
      [{key: 'x-cell-recaptcha-token', value: data.recaptchaToken}],
    );
    return result.Body;
  } catch (error) {
    return error;
  }
});

const initialState = {
  form: {
    id: '',
    CardDueYear: '',
    CardDueMonth: '',
    valid: '',
    errorObj: null,
    errors: {},
    rules: {},
    confirmationNumber: '',
  },
  CGData: {},
  CCData: {},
  CCType: '',
  CCToken: '',
  CCvv: '',
  Credit4Digits: '',
  isTermsAccepted: false,
  isTermsMsg: false,
  CCError: false,
  iframeUrl: '',
  paymentCount: '',
  emvStep2CompletedSuccessfully: false,
  isThereTryAgainRequest: false,
  isEmvStep2Pending: false,
};

const PaymentCGSlice = createSlice({
  name: 'paymentCG',
  initialState: initialState,
  reducers: {
    resetStore: (state) => initialState,
    validateHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const validate = validateFields(lodash.cloneDeep(payload));
        return {payload: validate};
      },
    },

    changeHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const name = payload.target.name;
        const value = payload.target.value;
        const form = lodash.cloneDeep(payload.form);
        const validate = validateField(form, name, value);
        return {payload: validate};
      },
    },
    onCheck: {
      reducer(state, action) {
        if (!state.isTermsAccepted) state.isTermsAccepted = true;
      },
    },
    onUncheck: {
      reducer(state, action) {
        if (state.isTermsAccepted) state.isTermsAccepted = false;
      },
    },
    termsMsg: {
      reducer(state, action) {
        state.isTermsMsg = action.payload;
      },
    },
    isCreditCardError: {
      reducer(state, action) {
        state.CCError = action.payload;
      },
    },
    creditCardData: {
      reducer(state, action) {
        state.CCData = action.payload;
      },
    },
    setId: {
      reducer(state, action) {
        if (state.form.id === '') {
          state.form.id = action.payload;
        }
      },
    },
    setPaymentCount: {
      reducer(state, action) {
        if (action.payload) {
          state.form.paymentCount = action.payload;
        }
      },
    },
    initValidation: {
      reducer(state, action) {
        state.form.valid = '';
      },
    },
    clearUrl: {
      reducer(state, action) {
        state.iframeUrl = '';
      },
    },
    setTryAgainRequest: {
      reducer(state, action) {
        state.isThereTryAgainRequest = action.payload;
      },
    },
    setEmvStep2StatusToFailure: {
      reducer(state) {
        state.emvStep2CompletedSuccessfully = false;
      },
    },
    setCgIframeError: {
      reducer(state, action) {
        state.form.errorObj = action.payload;
      },
    },
  },
  extraReducers: {
    [getEMVStep2.pending]: (state) => {
      state.isEmvStep2Pending = true;
    },
    [getEMVStep1.fulfilled]: (state, action) => {
      let errorCode = 0;

      if (action.payload.errorCode != '00' && action.payload.paymentCGUrl) {
        state.iframeUrl = action.payload.paymentCGUrl;
      } else {
        state.form.errorObj = {errorId: 99, message: action.payload.returnCodeMessage};
      }
    },
    [getEMVStep2.fulfilled]: (state, action) => {
      state.isEmvStep2Pending = false;
      if (action.payload.errorCode != '00') {
        if (action.payload.authNumber != null && action.payload.authNumber != undefined) {
          state.emvStep2CompletedSuccessfully = true;
          state.form.confirmationNumber = action.payload.authNumber;
          state.form.CardDueYear = action.payload.cardYear;
          state.form.CardDueMonth = action.payload.cardMonth;
          state.form.id = action.payload.id;
          state.CGData = action.payload;
          state.CCToken = action.payload.ccToken;
          state.CCType = action.payload.ccType;
          state.Credit4Digits = action.payload.credit4Digits;
          state.paymentCount = action.payload.totalPayments;
          state.form.errorObj = null;
          if (action.payload.cvv != null && action.payload.cvv != undefined) state.CCvv = action.payload.cvv;
          state.form.errorObj = null;
        } else {
          state.form.errorObj = {errorId: 10, message: action.payload.returnCodeMessage};
        }
      } else {
        state.form.errorObj = {errorId: 10, message: action.payload.returnCodeMessage};
      }
    },
    [getEMVStep1.rejected]: (state, action) => {
      state.emvStep2CompletedSuccessfully = false;
    },
    [getEMVStep2.rejected]: (state, action) => {
      state.emvStep2CompletedSuccessfully = false;
      state.isEmvStep2Pending = false;
    },
  },
});

export const {
  changeHandler,
  resetStore,
  validateHandler,
  onCheck,
  onUncheck,
  termsMsg,
  isCreditCardError,
  creditCardData,
  setId,
  setPaymentCount,
  initValidation,
  clearUrl,
  setTryAgainRequest,
  setEmvStep2StatusToFailure,
  setCgIframeError,
} = PaymentCGSlice.actions;

export default PaymentCGSlice.reducer;
